import type { Document, TopLevelBlock } from '@contentful/rich-text-types';
import { zip } from 'lodash';
import type { CmsPageCollectionItems } from '$cms/types';

const COLLECTION_RICH_TEXT_KEY = [
  'description',
  'knownIssues',
  'frequency',
  'itemsReplaced',
  'symptoms',
] as const;

type CollectionRichTextKey = (typeof COLLECTION_RICH_TEXT_KEY)[number];

/**
 * Given CollectionItems or a RichText Document, return a list of RichText blocks
 * that can be transformed into sections by `chunkRichTextBlocksInSections()`.
 */
export const prepareCollectionsRichTextContent = (
  collectionItemsOrRichText: CmsPageCollectionItems | Document
): TopLevelBlock[] => {
  let blocks: TopLevelBlock[];

  if ('nodeType' in collectionItemsOrRichText) {
    /**
     * If a RichText is provided, it overrides the content of CollectionItems.
     */
    const overrideRichText = collectionItemsOrRichText;
    blocks = overrideRichText.content;
  } else {
    const collectionItems = collectionItemsOrRichText;

    const fieldBlockMap = new Map(
      zip(
        COLLECTION_RICH_TEXT_KEY,
        Array(COLLECTION_RICH_TEXT_KEY.length)
          .fill(null)
          .map(() => [])
      ) as [CollectionRichTextKey, TopLevelBlock[]][]
    );
    Object.values(collectionItems).forEach((collectionItem) => {
      if (typeof collectionItem !== 'object') return;
      if ('description' in collectionItem && collectionItem.description) {
        fieldBlockMap.get('description')?.push(...collectionItem.description.content);
      }
      if ('knownIssues' in collectionItem && collectionItem.knownIssues && !('product' in collectionItems)) {
        fieldBlockMap.get('knownIssues')?.push(...collectionItem.knownIssues.content);
      }
      if ('frequency' in collectionItem && collectionItem.frequency) {
        fieldBlockMap.get('frequency')?.push(...collectionItem.frequency.content);
      }
      if ('itemsReplaced' in collectionItem && collectionItem.itemsReplaced) {
        fieldBlockMap.get('itemsReplaced')?.push(...collectionItem.itemsReplaced.content);
      }
      if ('symptoms' in collectionItem && collectionItem.symptoms) {
        fieldBlockMap.get('symptoms')?.push(...collectionItem.symptoms.content);
      }
    });

    blocks = COLLECTION_RICH_TEXT_KEY.flatMap((field) => fieldBlockMap.get(field) || []);
  }

  return blocks;
};
